import React, { ReactNode } from "react";
import styled from "styled-components";

/**
 * SOREMO 共通コンテンツタグ（旧 `Tag theme="secondary"` の後継）。
 *
 * 目的: **補足情報をワードで添える**（ハッシュタグ等）。
 *  - プロフィール作品のハッシュタグ（表示 / `ContentTagInput` で入力）
 *  - 一覧テーブルのタグ列、求人票のタグ 等
 *
 * タグ系コンポーネントの使い分けは `FilterChip` のコメントを参照。
 *
 * 視覚仕様:
 *  - pill 型（radius 999px）・light-gray 文字・background 地・border 枠
 *  - 既定は表示専用（`<span>`、クリック不可）
 *  - `onRemove` を渡すと右端に × ボタンが付く（`ContentTagInput` 用）
 */
export type ContentTagSize = "small" | "medium";

export interface ContentTagProps {
  children: ReactNode;
  /** タグサイズ。既定 "small"。`size` は padding にのみ反映する。 */
  size?: ContentTagSize;
  /** 指定すると右端に × ボタンを表示する（タグ入力 UI 用）。 */
  onRemove?: () => void;
  /** × ボタンの aria-label。既定「削除」。 */
  removeLabel?: string;
  className?: string;
}

const SIZE_DEFAULT = {
  small: { fontClass: "bodytext-13", padding: "4px 8px" },
  medium: { fontClass: "bodytext-13", padding: "4px 12px" },
} as const;

const StyledTag = styled.span.attrs<{ $size: ContentTagSize }>(({ $size }) => ({
  className: SIZE_DEFAULT[$size].fontClass,
}))<{ $size: ContentTagSize }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  border-radius: 999px;
  border: 1px solid var(--soremo-border);
  padding: ${({ $size }) => SIZE_DEFAULT[$size].padding};
  white-space: nowrap;
  line-height: 1;
  color: var(--soremo-light-gray);
  background-color: var(--soremo-background);
`;

const RemoveButton = styled.button.attrs({
  type: "button",
})`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  appearance: none;
  border: none;
  margin: 0;
  padding: 0;
  background: transparent;
  color: inherit;
  cursor: pointer;
  line-height: 1;

  &:hover {
    color: var(--soremo-gray);
  }

  &:focus-visible {
    outline: 2px solid var(--soremo-blue);
    outline-offset: 1px;
    border-radius: 50%;
  }
`;

const RemoveIcon = styled.span.attrs({
  className: "material-symbols-rounded",
})<{ $size: ContentTagSize }>`
  font-size: ${({ $size }) => ($size === "small" ? "14px" : "16px")};
  line-height: 1;
  color: inherit;
`;

const ContentTag: React.FC<ContentTagProps> = ({
  children,
  size = "small",
  onRemove,
  removeLabel = "削除",
  className,
}) => {
  return (
    <StyledTag $size={size} className={className}>
      {children}
      {onRemove && (
        <RemoveButton onClick={onRemove} aria-label={removeLabel}>
          <RemoveIcon $size={size}>close</RemoveIcon>
        </RemoveButton>
      )}
    </StyledTag>
  );
};

export default ContentTag;
